import {
  Button,
  Calendar,
  InteractableComponent,
  Text,
} from '@shared/components';
import { CalendarProps } from '@shared/components/calendar/Calendar';
import { useInterval } from '@shared/hooks/useInterval';
import {
  makeElementClassNameFactory,
  makeRootClassName,
  StyleProps,
} from '@shared/utils';
import clsx from 'clsx';
import {
  addDays,
  addYears,
  endOfToday,
  endOfYesterday,
  format,
  startOfToday,
  startOfYesterday,
  subDays,
  subYears,
} from 'date-fns';
import { isArray } from 'lodash';
import { ReactElement, useState } from 'react';

export type PresetOption = {
  label: string;
  value: CalendarProps['value'];
};

export type DatePickerProps = StyleProps &
  Pick<CalendarProps, 'showDoubleView'> & {
    /**
     * The values of the date picker
     * @default []
     */
    values: CalendarProps['value'];
    /**
     * The format of the date picker display
     * @default 'MMM d, yyyy'
     */
    format?: string;
    /**
     * Whether the calendar dropdown and changing the date values is disabled
     * @default false
     */
    isDisabled?: boolean;
    /**
     * The change handler callback
     */
    onChange?: (newDate: CalendarProps['value']) => void;

    /**
     * Handler to call when user confirms the date selection
     */
    onSubmit?: (newDate: CalendarProps['value']) => void;

    /**
     * Whether to show a side menu of time presets
     * @default false
     */
    showTimePresetsNav?: boolean;

    /**
     * The time presets to show in the side menu
     */
    timePresetOptions?: PresetOption[];

    /**
     * Handler to clear the selected dates.
     */
    handleClearSelection?: () => void;
  };

const ROOT = makeRootClassName('DatePicker');
const el = makeElementClassNameFactory(ROOT);

// NOTE(parlato): Using these directly creates a poor UX where date
// components cache their dates from the moment the component is mounted.
export const POTENTIALLY_STALE_DEFAULT_PRESET_OPTIONS: PresetOption[] = [
  { label: 'Today', value: [startOfToday(), new Date()] },
  { label: 'Yesterday', value: [startOfYesterday(), endOfYesterday()] },
  { label: 'Past 7 Days', value: [subDays(new Date(), 7), new Date()] },
  { label: 'Past 30 Days', value: [subDays(new Date(), 30), new Date()] },
  { label: 'Past 90 Days', value: [subDays(new Date(), 90), new Date()] },
  { label: 'Past Year', value: [subYears(new Date(), 1), new Date()] },
];

export const getFreshPresetOptions = (): PresetOption[] => {
  return [
    { label: 'Today', value: [startOfToday(), new Date()] },
    { label: 'Yesterday', value: [startOfYesterday(), endOfYesterday()] },
    { label: 'Past 7 Days', value: [subDays(new Date(), 7), new Date()] },
    { label: 'Past 30 Days', value: [subDays(new Date(), 30), new Date()] },
    { label: 'Past 90 Days', value: [subDays(new Date(), 90), new Date()] },
    { label: 'Past Year', value: [subYears(new Date(), 1), new Date()] },
  ];
};

// NOTE(parlato): Using these directly creates a poor UX where date
// components cache their dates from the moment the component is mounted.
export const POTENTIALLY_STALE_FUTURE_PRESET_OPTIONS: PresetOption[] = [
  { label: 'Today', value: [startOfToday(), endOfToday()] },
  {
    label: 'Tomorrow',
    value: [addDays(startOfToday(), 1), addDays(endOfToday(), 1)],
  },
  { label: 'Next 7 Days', value: [startOfToday(), addDays(endOfToday(), 6)] },
  { label: 'Next 30 Days', value: [startOfToday(), addDays(endOfToday(), 29)] },
  { label: 'Next 90 Days', value: [startOfToday(), addDays(endOfToday(), 89)] },
  { label: 'Next Year', value: [startOfToday(), addYears(endOfToday(), 1)] },
];

const DEFAULT_PROPS = {
  format: 'MMM d, yyyy',
  isDisabled: false,
  timePresetOptions: POTENTIALLY_STALE_DEFAULT_PRESET_OPTIONS,
} as const;

const toDate = (value: string | Date): Date =>
  value instanceof Date ? value : new Date(value);

const getDisplayForValues = (values: CalendarProps['value'], f: string) => {
  if (isArray(values)) {
    const [start, end] = values;
    if (start && end) {
      return `${format(toDate(start), f)} - ${format(toDate(end), f)}`;
    }
    if (start) {
      return `${format(toDate(start), f)}`;
    }
    if (end) {
      return `${format(toDate(end), f)}`;
    }
    return '';
  }
};

export function DatePicker(props: DatePickerProps): ReactElement {
  const p = { ...DEFAULT_PROPS, ...props };
  const [timePresetOptions, setTimePresetOptions] = useState<PresetOption[]>(
    p.timePresetOptions ?? getFreshPresetOptions()
  );
  useInterval(() => {
    setTimePresetOptions(p.timePresetOptions ?? getFreshPresetOptions());
  }, 30 * 1000);

  return (
    <div className={clsx(ROOT, p.className)}>
      <div className={el`calendar-with-presets`}>
        <aside className={el`presets`}>
          <Text type="body-sm" isHeavy>
            Time Presets
          </Text>
          {timePresetOptions.map((option) => (
            <InteractableComponent
              key={option.label}
              onPress={() => p.onChange?.(option.value)}
            >
              <Text type="body-sm">{option.label}</Text>
            </InteractableComponent>
          ))}
        </aside>
        <Calendar
          value={p.values}
          onChange={(newDate: CalendarProps['value']) => p.onChange?.(newDate)}
          selectRange
          showDoubleView
          className={el`calendar`}
          locale="en-US"
        />
      </div>
      <footer className={el`footer`}>
        <Text type="body-xs">{getDisplayForValues(p.values, p.format)}</Text>
        <div className={el`actions`}>
          <Button onPress={p.handleClearSelection} variant="default">
            Clear selection
          </Button>
          <Button
            variant="primary"
            onPress={() => {
              p.onSubmit?.(p.values);
            }}
          >
            Confirm Selection
          </Button>
        </div>
      </footer>
    </div>
  );
}
export default DatePicker;
